import { autoUpdate, offset, useClick, useFloating, useInteractions } from '@floating-ui/react';
import debounce from 'lodash.debounce';
import { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import useUser from '@alltrails/context/hooks/useUser';
import { SPACE_200 } from '@alltrails/denali/tokens';
import SignUpModal from '@alltrails/user-auth/components/SignUpModal';
import useStyleKeyboardShortcuts from '../../hooks/useStyleKeyboardShortcuts';
import useStyleSelection from '../../hooks/useStyleSelection';
import type { StyleId } from '../../types/Styles';
import LayerButton from '../LayerButton';
import LayerMenu from '../LayerMenu';

type StyleButtonProps = {
  isStyleModalInitiallyOpen?: boolean;
  onStyleModalChange?: (isOpen: boolean) => void;
} & Omit<Parameters<typeof useStyleSelection>[0], 'handleRoadblock'> &
  Pick<ComponentProps<typeof LayerMenu>, 'mapLocation' | 'pauseFocusTrap'>;

const StyleButton = ({
  disableAnalytics,
  isStyleModalInitiallyOpen = false,
  onMapBaseLayerTapped,
  onMapOverlayTapped,
  onStyleModalChange,
  handleFailedEntitlementCheck,
  mapLocation,
  pauseFocusTrap
}: StyleButtonProps) => {
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [styleIdAfterRoadblock, setStyleIdAfterRoadblock] = useState<StyleId | undefined>(undefined);
  const user = useUser();

  const handleRoadblock = useCallback(
    (styleId?: StyleId) => {
      if (!user) {
        setIsSignupModalOpen(true);
        setStyleIdAfterRoadblock(styleId);
      }
    },
    [user]
  );

  const { hasPermission } = useAuthorization();

  const { onStyleSelect } = useStyleSelection({
    onMapBaseLayerTapped,
    onMapOverlayTapped,
    disableAnalytics,
    handleFailedEntitlementCheck,
    handleRoadblock
  });

  // Note that this is defined outside the scope of the style modal. These
  // shortcuts are meant to be used without having to open the modal
  useStyleKeyboardShortcuts(hasPermission({ permission: 'trails:manage' }) ? onStyleSelect : undefined);

  const [isStyleModalOpen, setIsStyleModalOpen] = useState(isStyleModalInitiallyOpen);

  // Debounced to handle race conditions when clicking on the layer button while the layer menu is already open.
  // This can close the menu via focus trap deactivation before the button has a chance to handle the click, which opens it again.
  const updateModalStatus = useMemo(
    () =>
      debounce((isOpen: boolean) => {
        onStyleModalChange?.(isOpen);
        setIsStyleModalOpen(isOpen);
      }, 50),
    [onStyleModalChange]
  );

  const { refs, floatingStyles, context } = useFloating({
    placement: 'left-start',
    middleware: [offset({ mainAxis: SPACE_200, crossAxis: 0 })],
    whileElementsMounted: autoUpdate
  });

  const click = useClick(context);

  const { getFloatingProps } = useInteractions([click]);

  useEffect(() => {
    if (user && styleIdAfterRoadblock) {
      onStyleSelect(styleIdAfterRoadblock);
      setStyleIdAfterRoadblock(undefined);
    }
  }, [onStyleSelect, styleIdAfterRoadblock, user]);

  return (
    <>
      <LayerButton ref={refs.setReference} isOpen={isStyleModalOpen} onClick={updateModalStatus} />
      {isStyleModalOpen && (
        <LayerMenu
          ref={refs.setFloating}
          disableAnalytics={disableAnalytics}
          isOpen={isStyleModalOpen}
          mapLocation={mapLocation}
          onCloseRequest={() => {
            updateModalStatus(false);
          }}
          onMapBaseLayerTapped={onMapBaseLayerTapped}
          onMapOverlayTapped={onMapOverlayTapped}
          handleFailedEntitlementCheck={handleFailedEntitlementCheck}
          handleRoadblock={handleRoadblock}
          desktopModalProps={{ ...getFloatingProps(), style: floatingStyles }}
          pauseFocusTrap={pauseFocusTrap}
        />
      )}
      {isSignupModalOpen && (
        <SignUpModal
          closeModal={() => {
            setIsSignupModalOpen(false);
          }}
          isOpen={isSignupModalOpen}
          onSuccess={() => {
            setIsSignupModalOpen(false);
          }}
          trigger="Select Map Overlay"
          upsellTrigger="Select Map Overlay"
        />
      )}
    </>
  );
};

export default StyleButton;
